import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon, Loader2Icon } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { passwordSchema } from '@shared/schemas/auth';
import { resetPassword } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { AnimatedBrandLogo } from '@/components/animated-brand-logo';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const inputAuthClass =
  'h-12 rounded-lg border border-zinc-200 bg-white px-4 text-[15px] text-zinc-900 shadow-xs transition-colors placeholder:text-zinc-400 hover:border-zinc-300 focus-visible:border-zinc-950 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-950 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:hover:border-zinc-700 dark:focus-visible:border-zinc-100 dark:focus-visible:ring-zinc-100';

const labelAuthClass = 'text-sm font-medium text-zinc-800 dark:text-zinc-200';

const resetPasswordFormSchema = z.object({
  confirmPassword: passwordSchema,
  password: passwordSchema,
}).refine((values) => values.password === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>;

type ResetPasswordFormProps = React.ComponentProps<'form'> & {
  token: string | null;
};

export function ResetPasswordForm({
  className,
  token,
  ...props
}: ResetPasswordFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const hasToken = useMemo(() => Boolean(token), [token]);
  const form = useForm<ResetPasswordFormValues>({
    defaultValues: {
      confirmPassword: '',
      password: '',
    },
    resolver: zodResolver(resetPasswordFormSchema),
  });

  async function handleSubmit(values: ResetPasswordFormValues) {
    form.clearErrors('root');

    if (!token) {
      form.setError('root', { message: 'Le lien de réinitialisation est invalide.' });
      return;
    }

    const result = await resetPassword(values.password, token);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    toast.success('Mot de passe mis à jour. Vous pouvez vous reconnecter.');
    navigate('/auth/login?reset=success', { replace: true });
  }

  return (
    <form
      data-testid="reset-password-form"
      className={cn('flex flex-col gap-0', className)}
      onSubmit={form.handleSubmit(handleSubmit)}
      {...props}
    >
      <FieldGroup className="gap-5">
        {/* Header Branding & Title */}
        <div className="auth-reveal-stagger-1 flex flex-col items-center pb-2 text-center">
          <Link
            to="/"
            className="mb-6 inline-flex items-center justify-center transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/10 rounded-lg"
            aria-label="Inersia - Accueil"
          >
            <AnimatedBrandLogo variant="hero" appearance="auto" className="h-10 w-auto sm:h-11 md:h-12" />
          </Link>
          <h1 className="text-balance text-2xl font-bold tracking-tight text-zinc-950 sm:text-[1.75rem] dark:text-zinc-50">
            Nouveau mot de passe
          </h1>
          <p className="mt-2 text-balance text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
            {hasToken
              ? 'Définissez maintenant votre nouveau mot de passe sécurisé pour Inersia.'
              : 'Le lien de réinitialisation est manquant ou a expiré.'}
          </p>
        </div>

        <Field className="auth-reveal-stagger-2 gap-2">
          <FieldLabel htmlFor="reset-password" className={labelAuthClass}>Nouveau mot de passe</FieldLabel>
          <div className="relative">
            <Input
              id="reset-password"
              data-testid="reset-password-input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={cn(inputAuthClass, 'pr-11')}
              aria-invalid={form.formState.errors.password ? 'true' : 'false'}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-0 flex h-full items-center px-3.5 text-zinc-400 hover:text-zinc-700 transition-colors focus:outline-none dark:text-zinc-500 dark:hover:text-zinc-300"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon className="size-4.5" /> : <EyeIcon className="size-4.5" />}
            </button>
          </div>
          <FieldDescription className="text-xs text-zinc-500">Au moins 8 caractères.</FieldDescription>
          <FieldError data-testid="reset-password-field-error" errors={[form.formState.errors.password]} />
        </Field>

        <Field className="auth-reveal-stagger-3 gap-2">
          <FieldLabel htmlFor="reset-confirm-password" className={labelAuthClass}>Confirmation</FieldLabel>
          <div className="relative">
            <Input
              id="reset-confirm-password"
              data-testid="reset-password-confirm-input"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              className={cn(inputAuthClass, 'pr-11')}
              aria-invalid={form.formState.errors.confirmPassword ? 'true' : 'false'}
              {...form.register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-0 top-0 flex h-full items-center px-3.5 text-zinc-400 hover:text-zinc-700 transition-colors focus:outline-none dark:text-zinc-500 dark:hover:text-zinc-300"
              aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOffIcon className="size-4.5" /> : <EyeIcon className="size-4.5" />}
            </button>
          </div>
          <FieldError errors={[form.formState.errors.confirmPassword]} />
        </Field>

        <FieldError data-testid="reset-password-root-error" errors={[form.formState.errors.root]} />

        <Field className="auth-reveal-stagger-4 pt-2">
          <Button
            data-testid="reset-password-submit"
            type="submit"
            size="lg"
            className="h-12 w-full rounded-lg bg-zinc-950 text-[15px] font-semibold text-white shadow-xs transition-all duration-150 hover:bg-zinc-800 active:scale-[0.99] dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
            disabled={!hasToken || form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2Icon className="size-4.5 animate-spin" /> : null}
            Enregistrer le nouveau mot de passe
          </Button>
        </Field>

        <div className="auth-reveal-stagger-5 pt-2 text-center">
          <Link
            to="/auth/login"
            className="text-sm font-normal text-zinc-500 hover:text-zinc-900 transition-colors dark:text-zinc-400 dark:hover:text-zinc-100"
          >
            Retour à la connexion
          </Link>
        </div>
      </FieldGroup>
    </form>
  );
}
